import {
  useFormContext,
  useWatch,
  Controller,
  FieldArrayWithId,
  UseFieldArrayRemove,
  UseFieldArrayAppend,
} from 'react-hook-form';
import { IoIosAddCircleOutline } from 'react-icons/io';
import Button from '../../../../assets/essentials/Button';
import Create from '../../../../helpers/modal/CreateModal';
import FormErrorMessage from '../../../../helpers/FormErrorMessage';
import CloseModal from '../../../../helpers/modal/CloseModal';
import Modal from '../../../../helpers/modal/Modal';
import OptionList from '../../../../helpers/options/OptionList';
import SearchableSelect from '../../../../helpers/searchableSelect/SearchableSelect';
import classes from './CreateBill.module.css';
import React from 'react';
import Decimal from 'decimal.js';
import { Bill } from './CreateBill';

type LineItemRowProps = {
  index: number;
  assets: AssetOption[];
  expenseChartOfAccounts: ChartOfAccount[];
  remove: (index: number) => void;
};

const LineItemRow = ({
  index,
  assets,
  expenseChartOfAccounts,
  remove,
}: LineItemRowProps) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<Bill>();

  const rate = useWatch({ control, name: `lineItems.${index}.rate` });
  const quantity = useWatch({
    control,
    name: `lineItems.${index}.quantity`,
  });
  const total = new Decimal(rate || 0).times(quantity || 0).toNumber();
  return (
    <tr className={classes.lineItemRow}>
      <td className={classes.coa}>
        <label htmlFor={`chartOfAccountId-${index}`}>Account</label>
        <Controller
          name={`lineItems.${index}.chartOfAccountId`}
          rules={{ required: 'A chart of account is required.' }}
          control={control}
          render={({ field, fieldState }) => (
            <>
              <SearchableSelect
                {...field}
                onChange={value =>
                  field.onChange(value === '' ? null : Number(value) || null)
                }
                placeholderText="-- Select Account --"
                value={field.value}
              >
                <OptionList
                  optionData={expenseChartOfAccounts}
                  attributeID="chartOfAccountId"
                  attributeName="accountName"
                  attributeGroup="type"
                />
              </SearchableSelect>
              <FormErrorMessage errorMessage={fieldState.error?.message} />
            </>
          )}
        />
      </td>
      <td className={classes.loadRental}>
        <label htmlFor={`loadRentalType-${index}`}>Load/Rental #</label>
        <Controller
          name={`lineItems.${index}.loadRentalType`}
          control={control}
          rules={{
            validate: (value, formValues) => {
              const loadRentalId = formValues.lineItems[index].loadRentalId;
              if (loadRentalId && !value) {
                return 'Specify load or rental.';
              }
              return true;
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <select {...field} id={`loadRentalType-${index}`}>
                <option value={''}>- N/A -</option>
                <option value="Load">Load</option>
                <option value="Rental">Rental</option>
              </select>
              <FormErrorMessage errorMessage={fieldState.error?.message} />
            </>
          )}
        />
        <label htmlFor={`loadRentalId-${index}`}>Load/Rental #</label>
        <Controller
          name={`lineItems.${index}.loadRentalId`}
          control={control}
          rules={{
            validate: (value, formValues) => {
              const loadRentalType = formValues.lineItems[index].loadRentalType;
              if (loadRentalType && !value) {
                return 'Load/Rental # is required when type is specified.';
              }
              return true;
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <input
                {...field}
                id={`loadRentalId-${index}`}
                type="number"
                onChange={e => {
                  const val = e.target.value;
                  field.onChange(val === '' ? null : val);
                }}
              />
              <FormErrorMessage errorMessage={fieldState.error?.message} />
            </>
          )}
        />
      </td>
      <td className={classes.asset}>
        <label htmlFor={`assetId-${index}`}>Asset</label>
        <Controller
          name={`lineItems.${index}.assetId`}
          control={control}
          render={({ field }) => (
            <SearchableSelect
              {...field}
              onChange={value =>
                field.onChange(value === '' ? null : Number(value) || null)
              }
              value={field.value}
            >
              <option value={''}>- N/A -</option>
              <OptionList
                optionData={assets}
                attributeID="assetId"
                attributeName="name"
                attributeGroup="group"
              />
            </SearchableSelect>
          )}
        />
      </td>
      <td>
        <textarea
          {...register(`lineItems.${index}.note`)}
          className={classes.noteTextArea}
        />
      </td>
      <td>
        <input
          {...register(`lineItems.${index}.rate`, {
            required: 'A rate is required.',
            min: { value: 0.01, message: 'Rate must be at least 0.01.' },
            setValueAs: v => (v === '' ? null : Number(v)),
          })}
          type="number"
          step={0.01}
          className={classes.rateInput}
        />
        <FormErrorMessage
          errorMessage={errors.lineItems?.[index]?.rate?.message}
        />
      </td>
      <td>
        <input
          {...register(`lineItems.${index}.quantity`, {
            required: 'A quantity is required.',
            min: { value: 0.01, message: 'Quantity must be at least 0.01.' },
            setValueAs: v => (v === '' ? null : Number(v)),
          })}
          type="number"
          step={0.01}
          className={classes.quantityInput}
        />
        <FormErrorMessage
          errorMessage={errors.lineItems?.[index]?.quantity?.message}
        />
      </td>
      <td>
        <input
          {...register(`lineItems.${index}.total`)}
          type="text"
          value={
            total
              ? total.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })
              : ''
          }
          className={classes.totalInput}
          disabled
        />
      </td>
      <td className={classes.deleteButton}>
        <button type="button" onClick={() => remove(index)}>
          ❌
        </button>
      </td>
    </tr>
  );
};

type LineItemsTableProps = {
  fields: FieldArrayWithId<Bill, 'lineItems', 'id'>[];
  remove: UseFieldArrayRemove;
  append: UseFieldArrayAppend<Bill, 'lineItems'>;
  expenseChartOfAccounts: ChartOfAccount[];
  assets: AssetOption[];
};

const LineItemsTable = ({
  fields,
  remove,
  append,
  expenseChartOfAccounts,
  assets,
}: LineItemsTableProps) => {
  const {
    control,
    reset,
    formState: { isSubmitting },
  } = useFormContext<Bill>();
  const lineItems = useWatch({ control, name: 'lineItems' });
  const totalSum = lineItems
    .map(item => new Decimal(item.rate || 0).times(item.quantity || 0))
    .reduce((sum, dec) => sum.plus(dec), new Decimal(0))
    .toNumber()
    .toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });

  const openClearDraftModal = () => {
    const handleClearDraft = () => {
      localStorage.removeItem('createBillDraft');
      reset();
      CloseModal();
    };

    return Create(
      <Modal>
        <div className={classes.clearDraftModal}>
          <p>Are you sure you want to clear the saved draft?</p>
          <div className={classes.clearDraftModalActions}>
            <Button onClick={handleClearDraft} variant="bad">
              Clear
            </Button>
            <Button onClick={CloseModal}>Cancel</Button>
          </div>
        </div>
      </Modal>,
    );
  };

  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Account</th>
            <th>Load/Rental #</th>
            <th>Asset</th>
            <th>Description</th>
            <th>Rate</th>
            <th>Quantity</th>
            <th>Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => (
            <LineItemRow
              key={field.id}
              index={index}
              remove={remove}
              expenseChartOfAccounts={expenseChartOfAccounts}
              assets={assets}
            />
          ))}
          <tr>
            <td colSpan={6} />
            <td className={classes.totalSum}>{'Bill Total: ' + totalSum}</td>
            <td />
          </tr>
        </tbody>
      </table>
      <div className={classes.buttonContainer}>
        <button
          type="button"
          onClick={() => {
            append({
              chartOfAccountId: null,
              note: '',
              loadRentalType: '',
              loadRentalId: null,
              assetId: null,
              rate: null,
              quantity: null,
              total: null,
            });
          }}
          className={classes.addLineItemButton}
        >
          <IoIosAddCircleOutline />
          Add New Line Item
        </button>
        <div className={classes.clearDraftModalActions}>
          <Button variant="good" type="submit" disabled={isSubmitting}>
            Submit
          </Button>
          <Button type="button" onClick={openClearDraftModal} variant="bad">
            Clear Draft
          </Button>
        </div>
      </div>
    </>
  );
};

export default LineItemsTable;
